import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  PairMatcherWidgetJSON,
  MatcherItemContent,
  LayoutItem,
  WidgetJSON,
} from '../../../transformers/zod-schemas';
import type { WidgetValidation } from '../../../validators';
import type { ValidationStatus } from '../types';
import { LayoutRenderer } from '../../components/LayoutRenderer';
import { JSON_TYPES } from '../../../constants';
import { shouldItemsUseRTL } from '../../utils/bidi-detection';
import { usePairLines } from './usePairLines';
import { useSound } from '../../sounds';
import { shuffle } from 'lodash';
import {
  type PairState,
  EMPTY_WIDGETS,
  EMPTY_VALUES,
  EMPTY_DROPPED,
  NOOP,
  buildInitialState,
  findTopForBottom,
  withLineDirection,
  getPairIds,
  withPair,
  resolveCardStatus,
  buildDotClass,
  buildCardClass,
  lineStrokeVar,
} from '../../utils/pair-matcher.utils';

export interface PairMatcherWidgetProps {
  widgetId: string;
  widget: PairMatcherWidgetJSON;
  onChange: (widgetId: string, value: string[][]) => void;
  validationStatus?: ValidationStatus | null;
  validationDetail?: WidgetValidation;
  /** Restore saved progress. Shape: [[topId, bottomId], ...] */
  defaultValue?: string[][];
  /** Widget-refs nested inside item content. */
  widgets?: Record<string, WidgetJSON>;
  /** Shuffle TOP/BOTTOM on mount (default true). Pass false for tests/review. */
  shuffleItems?: boolean;
}

/** Display-only item renderer. SPACE/GROUP short-circuit LayoutRenderer to avoid spurious onChange. */
function renderPairMatcherItemContent(
  item: LayoutItem,
  widgets: Record<string, WidgetJSON>,
): React.ReactNode {
  if (item.type === JSON_TYPES.SPACE) {
    return <span key={item.id} className="space"> </span>;
  }

  if (item.type === JSON_TYPES.GROUP) {
    return (
      <span key={item.id} className="group">
        {item.items.map((subItem: LayoutItem) => renderPairMatcherItemContent(subItem, widgets))}
      </span>
    );
  }

  return (
    <LayoutRenderer
      key={item.id}
      item={item}
      widgets={widgets}
      values={EMPTY_VALUES}
      droppedInputs={EMPTY_DROPPED}
      onChange={NOOP}
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export const PairMatcherWidget: React.FC<PairMatcherWidgetProps> = ({
  widgetId,
  widget,
  onChange,
  validationStatus = null,
  validationDetail,
  defaultValue,
  widgets,
  shuffleItems = true,
}) => {
  const { topItems, bottomItems } = widget;


  // Shuffle once on mount intentionally.
  // PairMatcher intentionally assumes widget item identity remains stable for the component lifetime;
  // reordering cards during student interaction would be a poor UX.
  const [shuffledTop] = useState<MatcherItemContent[]>(
    () => shuffleItems ? shuffle([...topItems]) : [...topItems],
  );
  const [shuffledBottom] = useState<MatcherItemContent[]>(
    () => shuffleItems ? shuffle([...bottomItems]) : [...bottomItems],
  );
  const widgetsMap = widgets ?? EMPTY_WIDGETS;
  const topIds = useMemo(() => new Set(topItems.map(item => item.itemId)), [topItems]);
  const bottomIds = useMemo(() => new Set(bottomItems.map(item => item.itemId)), [bottomItems]);
  const pairStatusByTopId = useMemo(() => {
    const statuses: Record<string, 'correct' | 'incorrect'> = {};
    for (const result of validationDetail?.groupResults ?? []) {
      const ids = getPairIds(result.group, topIds, bottomIds);
      if (ids) statuses[ids.topId] = result.isCorrect ? 'correct' : 'incorrect';
    }
    return statuses;
  }, [validationDetail?.groupResults, topIds, bottomIds]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [state, setState] = useState<PairState>(() => ({
    ...buildInitialState(defaultValue, topIds, bottomIds),
    pending: null,
    flashTopId: null,
    flashBottomId: null,
  }));
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Sound — derive events from state transitions (reducers stay pure) ──────
  const playSound = useSound();
  const soundPrevRef = useRef({ count: 0, pendingId: null as string | null, flashId: null as string | null });
  const soundMounted = useRef(false);
  useEffect(() => {
    const count = Object.keys(state.pairs).length;
    const pendingId = state.pending?.id ?? null;
    const flashId = state.flashTopId;
    const prev = soundPrevRef.current;
    if (!soundMounted.current) {
      soundMounted.current = true; // baseline only — don't sound on restore/mount
    } else if (flashId !== null && flashId !== prev.flashId) {
      // Track the flashed id, not a bool: a rapid re-pair sets a new flashTopId
      // while the old flash is still up, so a bool edge would miss it.
      // Re-pair keeps pair count stable, so `count < prev.count` is false here —
      // the else-if branches are mutually exclusive by reducer invariant.
      playSound('pair-link');        // new pair or re-pair (covers unlink+link)
    } else if (count < prev.count) {
      playSound('pair-unlink');      // bare unpair
    } else if (pendingId !== null && pendingId !== prev.pendingId) {
      playSound('pair-select');      // card picked, awaiting partner
    }
    soundPrevRef.current = { count, pendingId, flashId };
  }, [state.pairs, state.pending, state.flashTopId, playSound]);

  const handleTopClick = useCallback((topId: string) => {
    if (validationStatus) return;
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
    setState(prev => {
      if (prev.pairs[topId] !== undefined) {
        // Paired → unpair, re-pair with pending bottom if any
        const { [topId]: _, ...rest } = prev.pairs;
        const { [topId]: __, ...restDirections } = prev.lineDirections;
        if (prev.pending?.side === 'bottom') {
          return {
            pairs: withPair(rest, topId, prev.pending.id),
            lineDirections: withLineDirection(restDirections, rest, topId, prev.pending.id, 'bottom-to-top'),
            pending: null,
            flashTopId: topId,
            flashBottomId: prev.pending.id,
          };
        }
        return {
          pairs: rest,
          lineDirections: restDirections,
          pending: { side: 'top', id: topId },
          flashTopId: null,
          flashBottomId: null,
        };
      }
      if (prev.pending?.side === 'bottom') {
        return {
          pairs: withPair(prev.pairs, topId, prev.pending.id),
          lineDirections: withLineDirection(prev.lineDirections, prev.pairs, topId, prev.pending.id, 'bottom-to-top'),
          pending: null,
          flashTopId: topId,
          flashBottomId: prev.pending.id,
        };
      }
      // Toggle pending selection
      return {
        ...prev,
        pending: prev.pending?.side === 'top' && prev.pending.id === topId
          ? null
          : { side: 'top', id: topId },
        flashTopId: null,
        flashBottomId: null,
      };
    });
  }, [validationStatus]);

  const handleBottomClick = useCallback((bottomId: string) => {
    if (validationStatus) return;
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
    setState(prev => {
      // Paired → remove, re-pair with pending top if any
      const existingTop = findTopForBottom(prev.pairs, bottomId);
      if (existingTop !== undefined) {
        const { [existingTop]: _, ...rest } = prev.pairs;
        const { [existingTop]: __, ...restDirections } = prev.lineDirections;
        if (prev.pending?.side === 'top' && prev.pending.id !== existingTop) {
          return {
            pairs: withPair(rest, prev.pending.id, bottomId),
            lineDirections: withLineDirection(restDirections, rest, prev.pending.id, bottomId, 'top-to-bottom'),
            pending: null,
            flashTopId: prev.pending.id,
            flashBottomId: bottomId,
          };
        }
        return {
          pairs: rest,
          lineDirections: restDirections,
          pending: { side: 'bottom', id: bottomId },
          flashTopId: null,
          flashBottomId: null,
        };
      }
      // New pair
      if (prev.pending?.side === 'top') {
        return {
          pairs: withPair(prev.pairs, prev.pending.id, bottomId),
          lineDirections: withLineDirection(prev.lineDirections, prev.pairs, prev.pending.id, bottomId, 'top-to-bottom'),
          pending: null,
          flashTopId: prev.pending.id,
          flashBottomId: bottomId,
        };
      }
      return {
        ...prev,
        pending: prev.pending?.side === 'bottom' && prev.pending.id === bottomId
          ? null
          : { side: 'bottom', id: bottomId },
        flashTopId: null,
        flashBottomId: null,
      };
    });
  }, [validationStatus]);

  useEffect(() => {
    if (validationStatus || state.flashTopId === null || state.flashBottomId === null) return;
    flashTimerRef.current = setTimeout(() => {
      flashTimerRef.current = null;
      setState(current => {
        if (current.flashTopId === null && current.flashBottomId === null) return current;
        return { ...current, flashTopId: null, flashBottomId: null };
      });
    }, 650);

    return () => {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = null;
      }
    };
  }, [state.flashTopId, state.flashBottomId, validationStatus]);

  // ── Notify parent — skip first render to avoid overwriting defaultValue ───
  // Contract: ExerciseRenderer pre-seeds PAIR_MATCHER values, so the parent never sees `undefined` before first interaction.
  // External consumers should initialize the widget value as well before allowing submit.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const value: string[][] = Object.entries(state.pairs).map(([topId, bottomId]) =>
      state.lineDirections[topId] === 'bottom-to-top'
        ? [bottomId, topId]
        : [topId, bottomId]
    );
    onChangeRef.current(widgetId, value);
  }, [state.pairs, state.lineDirections, widgetId]);

  // ── SVG connector lines ───────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const { dotRefs, lines } = usePairLines({
    containerRef,
    pairs: state.pairs,
    lineDirections: state.lineDirections,
    validationStatus,
    pairStatusByTopId,
  });

  // ── Render ──────────────────────────────────────────────────────────────────
  const cols = useMemo(
    () => Math.max(topItems.length, bottomItems.length, 1),
    [topItems.length, bottomItems.length],
  );

  const renderCard = (item: MatcherItemContent, isTop: boolean) => {
    const isRtl = shouldItemsUseRTL(item.content);
    const pairedTopId = isTop ? item.itemId : findTopForBottom(state.pairs, item.itemId);
    const pairStatus = pairedTopId ? pairStatusByTopId[pairedTopId] ?? null : null;
    const status = resolveCardStatus(
      item.itemId, isTop,
      state.pairs, state.pending,
      validationStatus,
      pairStatus,
    );
    const isFlashing = isTop
      ? state.flashTopId === item.itemId
      : state.flashBottomId === item.itemId;
    const dotKey = isTop ? `top:${item.itemId}` : `bottom:${item.itemId}`;
    const dot = (
      <span
        className={buildDotClass(status, isTop ? 'south' : 'north')}
        ref={el => { if (el) dotRefs.current.set(dotKey, el); else dotRefs.current.delete(dotKey); }}
      />
    );
    return (
      <div key={item.id} className="pair-matcher-cell">
        {/* north dot (bottom row only) */}
        {!isTop && dot}

        <button
          type="button"
          className={`${buildCardClass(status, isFlashing)}${isRtl ? ' pair-matcher-card--rtl' : ''}`}
          dir={isRtl ? 'rtl' : undefined}
          onClick={() => isTop ? handleTopClick(item.itemId) : handleBottomClick(item.itemId)}
          aria-pressed={status !== 'idle'}
          aria-disabled={validationStatus ? 'true' : undefined}
        >
          <span className="pair-matcher-card-content">
            {item.content.map(li => renderPairMatcherItemContent(li, widgetsMap))}
          </span>
        </button>

        {/* south dot (top row only) */}
        {isTop && dot}
      </div>
    );
  };

  return (
    <div
      className={`pair-matcher-widget${validationStatus ? ` pair-matcher-widget--${validationStatus}` : ''}`}
      style={{ '--pm-cols': cols } as React.CSSProperties}
      ref={containerRef}
      role="group"
      aria-label="Pair matcher exercise"
    >
      {/* Top row */}
      <div className="pair-matcher-row pair-matcher-row--top">
        {shuffledTop.map(item => renderCard(item, true))}
      </div>

      {/* SVG overlay — pointer-events:none */}
      <svg className="pair-matcher-lines" aria-hidden="true">
        {lines.map(line => (
          <line
            key={line.topId}
            x1={line.x1} y1={line.y1}
            x2={line.x2} y2={line.y2}
            stroke={lineStrokeVar(line.status)}
            strokeWidth="var(--pm-line-width, 4)"
            strokeLinecap="round"
            pathLength={1}
            className="pair-matcher-line"
          />
        ))}
      </svg>

      {/* Bottom row */}
      <div className="pair-matcher-row pair-matcher-row--bottom">
        {shuffledBottom.map(item => renderCard(item, false))}
      </div>
    </div>
  );
};

PairMatcherWidget.displayName = 'PairMatcherWidget';
